import { CompoundMenuNode, MenuNode, MenuPath, RenderedMenuNode } from '@theia/core/lib/common/menu/menu-types';
import { CommonCommands } from '@theia/core/lib/browser';
import { useUIStore } from '@lved/ui';

/** Theia context key (boolean): true while auth, license, launcher, or toolchain overlays lock the shell. */
export const EDITOR_CHROME_LOCKED_CONTEXT_KEY: string = 'lvedEditorChromeLocked';

/** Body class toggled while the editor chrome is locked; drives locked-state shell styling. */
export const EDITOR_CHROME_LOCKED_BODY_CLASS: string = 'lvgl-editor-chrome-locked';

export const setEditorChromeLockedBodyClass = (locked: boolean): void => {
    if (typeof document === 'undefined') {
        return;
    }
    document.body.classList.toggle(EDITOR_CHROME_LOCKED_BODY_CLASS, locked);
};

/** Top-level menubar nodes that stay visible while the editor chrome is locked. */
export const EDITOR_CHROME_ALLOWED_TOP_LEVEL_MENU_IDS: ReadonlySet<string> = new Set<string>(['8_account', '9_help']);

/** DOM hosts that keep keyboard focus while the editor chrome is locked. */
export const EDITOR_CHROME_LOCK_HOST_IDS: ReadonlyArray<string> = ['lvgl-license-overlay', 'lvgl-launch-screen'];

export const APP_SHELL_INTERACTION_LOCK_SELECTORS: readonly string[] = [
    '#theia-left-right-split-panel',
    '#theia-statusBar',
    '.theia-statusBar'
];
export const LICENSE_GATE_BODY_CLASS: string = 'lvgl-license-gate-active';

/** Command IDs that match clipboard-style editor actions (paste still fires as a command in Theia). */
const CLIPBOARD_COMMAND_PATTERN = /clipboard|\.paste|\.cut|\.copy/i;

/** Command ID for Electron Toggle Developer Tools (Alt+F12). */
export const TOGGLE_DEVELOPER_TOOLS_COMMAND_ID = 'theia.toggleDevTools';

/** Commands reachable from Account / Help while locked. */
export const EDITOR_CHROME_ALLOWED_COMMAND_IDS: ReadonlySet<string> = new Set<string>([
    CommonCommands.ABOUT_COMMAND.id,
    TOGGLE_DEVELOPER_TOOLS_COMMAND_ID,
    'lvgl.license.openManager',
    'lvgl.license.logout',
    'lvgl.topActions.user.documentation',
    'lvgl.topActions.user.releaseNotes',
    'lvgl.topActions.user.reportIssue',
    'workbench.action.closeQuickOpen',
    'output:append',
    '_setContext'
]);

export const isEditorChromeLocked = (): boolean => useUIStore.getState().editorChromeLocked;

export const isEditableElement = (target: EventTarget | null): target is HTMLElement => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    if (target.isContentEditable) {
        return true;
    }
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

export const isCommandAllowedWhenEditorChromeLocked = (commandId: string): boolean => {
    if (EDITOR_CHROME_ALLOWED_COMMAND_IDS.has(commandId)) {
        return true;
    }
    const focused = document.activeElement;
    if (isEditableElement(focused) && CLIPBOARD_COMMAND_PATTERN.test(commandId)) {
        return true;
    }
    return false;
};

/** Blocks interaction with editor shell areas while preserving top-frame controls. */
export const syncAppShellInteractionLock = (): void => {
    if (typeof document === 'undefined') {
        return;
    }
    const blocked = isEditorChromeLocked() || document.body.classList.contains(LICENSE_GATE_BODY_CLASS);
    for (const selector of APP_SHELL_INTERACTION_LOCK_SELECTORS) {
        const elements: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(selector);
        for (const element of elements) {
            if (blocked) {
                element.setAttribute('inert', '');
                element.setAttribute('aria-hidden', 'true');
            } else {
                element.removeAttribute('inert');
                element.removeAttribute('aria-hidden');
            }
        }
    }
};

export const isInsideEditorChromeLockHost = (target: EventTarget | null): boolean => {
    if (!(target instanceof Node)) {
        return false;
    }
    return EDITOR_CHROME_LOCK_HOST_IDS.some((id) => document.getElementById(id)?.contains(target) === true);
};

export const shouldShowTopLevelMenuWhenLocked = (menuId: string): boolean =>
    EDITOR_CHROME_ALLOWED_TOP_LEVEL_MENU_IDS.has(menuId);

export const shouldIncludeMenuNodeWhenLocked = (menu: MenuNode, effectivePath: MenuPath): boolean => {
    if (!isEditorChromeLocked()) {
        return true;
    }
    if (CompoundMenuNode.is(menu) && RenderedMenuNode.is(menu)) {
        if (effectivePath.length === 2 && effectivePath[0] === 'menubar') {
            return shouldShowTopLevelMenuWhenLocked(menu.id);
        }
    }
    return true;
};
